import React from 'react';
import type { FieldValues } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  getColumns as getDisabilityInputColumns,
  getRows as getDisabilityInputRows,
} from '../../components/form/DisabilityInput';
import {
  getColumns as getEthnicityInputColumns,
  getRows as getEthnicityInputRows,
} from '../../components/form/EthnicityInput';
import {
  getColumns as getExceptionBreakdownColumns,
  getRows as getExceptionBreakdownRows,
} from '../../components/form/ExceptionBreakdownInput';
import {
  getColumns as getGenderInputColumns,
  getRows as getGenderInputRows,
} from '../../components/form/GenderInput';
import {
  getColumns as getFairAndOpenColumns,
  getRows as getFairAndOpenRows,
} from '../../components/form/InputFairAndOpen';
import {
  getColumns as getRecruitmentCampaignsColumns,
  getRows as getRecruitmentCampaignsRows,
} from '../../components/form/InputRecruitmentCampaigns';
import type { Page, Subsection } from '../../config/types';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import type { FetchFormResponse } from '../../service/types';
import { Status } from '../../types';
import {
  getSelectedConditionalQuestions,
  isPageCompleted,
  renderAnswer,
} from '../../utils/config-utils';
import { isJsonObject } from '../../utils/config-utils';
import Tag from '../Tag/Tag';

export const questionTypeToKeys = (
  questionType: Question['questionType'],
): { columns: string[]; rows: string[] } => {
  switch (questionType) {
    case 'exceptionBreakdownInput':
      return { columns: getExceptionBreakdownColumns(), rows: getExceptionBreakdownRows() };
    case 'fairAndOpenInput':
      return { columns: getFairAndOpenColumns(), rows: getFairAndOpenRows() };
    case 'genderInput':
      return { columns: getGenderInputColumns(), rows: getGenderInputRows() };
    case 'ethnicityInput':
      return { columns: getEthnicityInputColumns(), rows: getEthnicityInputRows() };
    case 'disabilityInput':
      return { columns: getDisabilityInputColumns(), rows: getDisabilityInputRows() };
    case 'recruitmentCampaignInput':
      return { columns: getRecruitmentCampaignsColumns(), rows: getRecruitmentCampaignsRows() };
    default:
      return { columns: [], rows: [] };
  }
};

// Convert a label to a title
// e.g. 'positionAdvertised' -> 'Position Advertised'
// e.g. 'grade6' -> 'Grade 6'
export const labelToTitle = (label: string) => {
  return label
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/([0-9])/g, ' $1') // Add space before numbers
    .replace(/^./, (str: string) => str.toUpperCase()) // Capitalize first letter
    .trim(); // Remove any extra spaces
};

export const renderJsonObject = (answer: Record<string, string>, question: Question) => {
  const { columns, rows } = questionTypeToKeys(question.questionType);

  // Render a table with the columns and rows
  return (
    <table className="govuk-table">
      <thead>
        <tr>
          <th> </th>
          {columns.map((column) => (
            <th key={column} style={{ padding: '12px' }}>
              {labelToTitle(column)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row}>
            <td style={{ padding: '12px', textTransform: 'capitalize' }}>{labelToTitle(row)}</td>
            {columns.length > 0 ? (
              columns.map((column) => (
                <td key={column} style={{ padding: '12px' }}>
                  {answer[`${row}${column}`] || '-'}
                </td>
              ))
            ) : (
              <td colSpan={columns.length} style={{ padding: '12px' }}>
                {answer[row] || '-'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const renderArrayAnswer = (answer: string[]) => {
  return (
    <ul className="govuk-list govuk-!-margin-0">
      {answer.map((a) => (
        <li key={a}>{a}</li>
      ))}
    </ul>
  );
};

export const parseAnswer = (
  answer: string | string[] | Record<string, string>,
  question: Question,
) => {
  if (Array.isArray(answer)) {
    return renderArrayAnswer(answer);
  }
  if (isJsonObject(answer)) {
    return renderJsonObject(answer as Record<string, string>, question);
  }
  return typeof answer === 'string' || typeof answer === 'number' ? answer : '';
};

export function renderSummaryRow(
  question: Question,
  formattedAnswers: string | string[] | Record<string, string>,
) {
  return (
    <tr className="govuk-table__row" key={question.name}>
      <td className="govuk-table__cell" style={{ padding: '12px' }}>
        {question.title}
      </td>
      <td className="govuk-table__cell" colSpan={2} style={{ height: '46px', padding: '12px' }}>
        {parseAnswer(formattedAnswers, question)}
      </td>
    </tr>
  );
}

export function renderListAnswers(formValues: FieldValues, questions: Question[]) {
  const listAnswers: { question: Question; questionName: string; answer: string }[] = [];

  for (const [questionName, answer] of Object.entries(formValues)) {
    const question = questions.find((q) => questionName.endsWith(q.name));
    if (question) listAnswers.push({ question, questionName, answer });
  }

  return (
    <>
      {listAnswers.map((listAnswer) => {
        const formattedAnswers = renderAnswer(listAnswer.question, listAnswer.answer);
        const showBullet =
          listAnswer.question.questionType === 'checkbox' ? 'govuk-list--bullet' : '';

        return (
          <React.Fragment key={listAnswer.question.name}>
            {renderSummaryRow(listAnswer.question, formattedAnswers)}
          </React.Fragment>
        );
      })}
    </>
  );
}

export const SummaryAnswers = ({
  pages,
  subsection,
  urlPath,
  displayTitle,
}: {
  pages: Page[];
  subsection: Subsection;
  urlPath: string;
  displayTitle?: boolean;
}) => {
  const { data } = useFormData();
  const answers: FetchFormResponse['answers'] = data?.answers || {};

  return (
    <table className="govuk-table">
      {displayTitle && (
        <caption className="govuk-table__caption govuk-table__caption--l">Your answers</caption>
      )}

      {pages.map(({ title, pageType, ...page }) => {
        const isDraft = !!data?.drafts?.[subsection.slug]?.includes(page.name);
        const questions = 'questions' in page ? page.questions || [] : [];
        const isCompleted = isPageCompleted(questions, answers)
          ? Status.Completed
          : Status.NotStarted;
        const status = isDraft ? Status.InProgress : isCompleted;

        return (
          <tbody className="govuk-table__body" key={subsection.slug}>
            <tr className="govuk-table__row">
              <th
                scope="row"
                className="govuk-table__header govuk-!-width-one-half"
                style={{ paddingTop: 30, paddingBottom: 15 }}
              >
                {title}
              </th>
              <td
                className="govuk-table__cell  govuk-!-width-one-quarter  govuk-!-width-one-quarter"
                data-testid="status"
                style={{ paddingTop: 30, paddingBottom: 15 }}
              >
                {pageType === 'question' && <Tag status={status} />}
              </td>
              <td
                className="govuk-table__cell"
                style={{ textAlign: 'right', paddingTop: 30, paddingBottom: 15 }}
              >
                <Link to={`${urlPath}/${page.name}`} className="govuk-link">
                  {pageType === 'information' && 'View'}
                  {pageType === 'list' && 'View'}
                  {pageType === 'question' &&
                    (status === Status.Completed ? 'Change' : 'Go to Question')}
                </Link>
              </td>
            </tr>
            {pageType === 'question' &&
              questions.map((question) => {
                const formattedAnswers = renderAnswer(question, answers[question.name]);
                const conditionalQuestions = getSelectedConditionalQuestions(
                  question,
                  answers[question.name],
                );
                const showBullet = question.questionType === 'checkbox' ? 'govuk-list--bullet' : '';

                return (
                  <React.Fragment key={question.name}>
                    {renderSummaryRow(question, formattedAnswers)}

                    {conditionalQuestions.map((conditionalQuestion) => {
                      const formattedConditionalAnswer = renderAnswer(
                        conditionalQuestion,
                        answers[conditionalQuestion.name],
                      );

                      return (
                        <React.Fragment key={conditionalQuestion.name}>
                          {renderSummaryRow(conditionalQuestion, formattedConditionalAnswer)}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}

            {pageType === 'list' && renderListAnswers(answers, questions)}
          </tbody>
        );
      })}
    </table>
  );
};
